// EASY USE:
// const [signupModalOpen, setSignupModalOpen] = useState(false);

// << a link/button that sets signupModalOpen to true on click >>

// {signupModalOpen && (
//     <SignupModal
//       open={signupModalOpen}
//       setOpen={setSignupModalOpen}
//       authType="signin" <-- change depending. can leave blank to use signup
//       afterAuthPath="/new-experiment" <-- change depending. can leave blank to use current location
//     />
//   )}

import { useCallback, useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useRouter } from "next/navigation";
import { AuthExperience } from "@neuraville/react-core-pro/auth";
import { useAuth } from "./auth";
import {
  trackAuthAttemptFailed,
  trackAuthProviderSelected,
  trackSignupModalOpened,
} from "../lib/analytics";
import {
  buildAuthFailureDiagnostics,
  classifyAuthFailureReason,
  resolveAuthProviderFromButtonLabel,
} from "./nrsAuthProviderClickTracking";
import { recordNrsDownloadFunnelEvent } from "./nrsDownloadFunnelEventsClient";

import {
  AUTH_SUCCESS_RECORDED_FLOW_KEY_PREFIX,
  PENDING_AUTH_PROVIDER_KEY,
} from "./nrsDownloadFunnelKeys";

const isExpectedAuthFailure = (error) => {
  const status = error?.status;
  const detail = error?.body?.detail;
  return (
    status === 401 ||
    status === 409 ||
    status === 428 ||
    detail === "INVALID_PASSWORD" ||
    detail === "EMAIL_NOT_FOUND"
  );
};

/**
 * @param {{
 *   open: boolean,
 *   setOpen: function,
 *   authType?: string,
 *   providerChooserFirst?: boolean,
 *   afterAuthPath?: string,
 *   onAuthComplete?: function,
 *   funnelFlowId?: string,
 *   funnelVisitId?: string,
 * }} props
 *
 * `providerChooserFirst` — with `authType="signup"`, show Google/GitHub/Neuraville
 * first instead of the email form; Neuraville then opens account creation.
 *
 * `onAuthComplete` — when provided, called after successful email/password auth
 * instead of `router.push(returnPath)`. OAuth flows always redirect via
 * `returnPath`; use sessionStorage on the landing page to detect the return.
 */
const SignupModal = ({
  open,
  setOpen,
  authType = "signup",
  providerChooserFirst = false,
  afterAuthPath = "",
  onAuthComplete,
  funnelFlowId,
  funnelVisitId,
}) => {
  const auth = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const url = `${window.location.origin}${pathname}${
    searchParams.toString() ? `?${searchParams}` : ""
  }`;
  const theme = useTheme();
  const isSmOrSmaller = useMediaQuery(theme.breakpoints.down("md"));
  const initialView =
    authType === "signin" || authType === "signup" || authType === "forgotpass"
      ? authType
      : "signup";
  const returnPath = afterAuthPath || url || window.location.pathname || "/";
  // Strictly track Composer pre-auth funnel only for tile-initiated flows
  // (identified by a per-click flow id). This prevents plain sign-in opens on
  // /nrs/download (e.g. navbar "Sign In") from inflating provider-selected
  // counts without a matching OS tile click.
  const shouldTrackDownloadFunnel =
    typeof funnelFlowId === "string" && funnelFlowId.length > 0;
  // Tracks the most recent chooser selection so we can attribute success/abandon
  // to the provider branch the user was on when leaving the modal.
  const lastProviderSelectedRef = useRef(null);
  // OAuth starts in one callback and may complete in another lifecycle step.
  const oauthInFlightProviderRef = useRef(null);
  const authSucceededRef = useRef(false);
  const abandonTrackedRef = useRef(false);
  const wasOpenRef = useRef(false);
  const isOpenRef = useRef(open);
  const pathnameRef = useRef(pathname || undefined);
  const flowIdRef = useRef(funnelFlowId);
  const visitIdRef = useRef(funnelVisitId);
  const shouldTrackRef = useRef(shouldTrackDownloadFunnel);

  const emitAbandonmentIfNeeded = useCallback(() => {
    if (!shouldTrackRef.current) {
      return;
    }
    if (authSucceededRef.current || abandonTrackedRef.current) {
      return;
    }
    const provider =
      oauthInFlightProviderRef.current || lastProviderSelectedRef.current || undefined;
    recordNrsDownloadFunnelEvent({
      eventType: "auth_flow_abandoned",
      provider,
      source: pathnameRef.current,
      flowId: flowIdRef.current,
      visitId: visitIdRef.current,
    });
    abandonTrackedRef.current = true;
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    lastProviderSelectedRef.current = null;
    oauthInFlightProviderRef.current = null;
    authSucceededRef.current = false;
    abandonTrackedRef.current = false;
  }, [open]);

  useEffect(() => {
    pathnameRef.current = pathname || undefined;
    flowIdRef.current = funnelFlowId;
    visitIdRef.current = funnelVisitId;
    shouldTrackRef.current = shouldTrackDownloadFunnel;
  }, [pathname, funnelFlowId, funnelVisitId, shouldTrackDownloadFunnel]);

  useEffect(() => {
    isOpenRef.current = open;
    if (wasOpenRef.current && !open) {
      // Capture parent-driven close paths where Dialog onClose may not fire.
      emitAbandonmentIfNeeded();
    }
    wasOpenRef.current = open;
  }, [open, emitAbandonmentIfNeeded]);

  useEffect(
    () => () => {
      // If the component unmounts while still open, record abandonment unless
      // we are in an OAuth redirect transition that will continue elsewhere.
      if (isOpenRef.current && !oauthInFlightProviderRef.current) {
        emitAbandonmentIfNeeded();
      }
    },
    [emitAbandonmentIfNeeded]
  );

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_NODE_ENV === "production") {
      trackSignupModalOpened(pathname || undefined);
    }
  }, [pathname]);

  const handleClose = () => {
    emitAbandonmentIfNeeded();
    setOpen(false);
  };

  /**
   * `AuthExperience`'s Google/GitHub/Neuraville chooser buttons don't expose
   * a "which option did they pick" callback (Google/GitHub route through
   * `onOAuthLogin`, but "Continue with Neuraville" is purely an internal
   * view-state change with no callback at all). Click-capture on the
   * container lets us observe all three consistently by matching the
   * rendered button label — see `resolveAuthProviderFromButtonLabel`.
   */
  const handleAuthChooserClickCapture = (event) => {
    const button =
      typeof event.target?.closest === "function"
        ? event.target.closest("button")
        : null;
    const provider = resolveAuthProviderFromButtonLabel(button?.textContent);
    if (provider) {
      lastProviderSelectedRef.current = provider;
      trackAuthProviderSelected({ provider, source: pathname || undefined });
      // Server-side echo so the admin KPI dashboard (Composer, not GA4) sees
      // this stage too.
      if (shouldTrackDownloadFunnel) {
        recordNrsDownloadFunnelEvent({
          eventType: "auth_provider_selected",
          provider,
          source: pathname || undefined,
          flowId: funnelFlowId,
          visitId: funnelVisitId,
        });
        try {
          sessionStorage.setItem(PENDING_AUTH_PROVIDER_KEY, provider);
        } catch {
          // sessionStorage unavailable
        }
      }
    }
  };

  /**
   * Tracks an auth attempt failure (GA4 + Composer) with a coarse, PII-free
   * reason. Called from both the OAuth (`onOAuthLogin`) and email/password
   * (`onAuthError`) paths so provider-level drop-off is visible regardless
   * of which continuation option the visitor picked.
   */
  const trackAuthFailure = (provider, error) => {
    lastProviderSelectedRef.current = provider;
    const reason = classifyAuthFailureReason(error);
    const metadata = buildAuthFailureDiagnostics(error);
    trackAuthAttemptFailed({ provider, reason, source: pathname || undefined });
    if (shouldTrackDownloadFunnel) {
      recordNrsDownloadFunnelEvent({
        eventType: "auth_attempt_failed",
        provider,
        reason,
        source: pathname || undefined,
        flowId: funnelFlowId,
        visitId: funnelVisitId,
        metadata,
      });
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth={isSmOrSmaller ? undefined : "sm"}
      open={open}
      onClose={handleClose}
      aria-labelledby="signup-modal"
      aria-describedby="modal-for-signup-or-login"
      slotProps={{
        paper: {
          style: {
            backgroundColor: "transparent",
            backgroundImage: "none",
            boxShadow: "none",
          },
        },
      }}
      sx={{
        "& .MuiDialog-paper": {
          margin: { xs: 1, sm: 2 },
          overflow: "visible",
          backgroundColor: "transparent !important",
          backgroundImage: "none !important",
          boxShadow: "none !important",
          border: "none !important",
          outline: "none !important",
          width: "auto",
          maxWidth: "calc(100vw - 16px)",
          maxHeight: "calc(100dvh - 16px)",
        },
      }}
    >
      <DialogContent sx={{ position: "relative", p: 0, bgcolor: "transparent" }}>
        <Box sx={{ p: 0 }}>
          <Paper
            elevation={0}
            onClickCapture={handleAuthChooserClickCapture}
            sx={{
              position: "relative",
              width: { xs: "calc(100vw - 16px)", sm: 440 },
              borderRadius: 0,
              border: "1px solid rgba(0, 0, 0, 0.18)",
              bgcolor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              p: 4,
              boxShadow: undefined,
              fontFamily: "inherit",
              textRendering: "optimizeLegibility",
              fontOpticalSizing: "auto",
              "& .MuiTextField-root .MuiInputBase-input": {
                color: "#000000",
              },
              "& .MuiTextField-root .MuiInputLabel-root": {
                color: "#4b5563",
              },
              "& .MuiTextField-root .MuiInputLabel-root.Mui-focused": {
                color: "#111827",
              },
              "& .MuiOutlinedInput-root fieldset": {
                borderColor: "#424242",
                borderWidth: "2px",
              },
              "& .MuiOutlinedInput-root:hover fieldset": {
                borderColor: "#616161",
              },
              "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                borderColor: "#212121",
                borderWidth: "2px",
              },
            }}
          >
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={(theme) => ({
                position: "absolute",
                right: 8,
                top: 8,
                color: theme.palette.grey[700],
                zIndex: 2,
              })}
            >
              <Close />
            </IconButton>
            <AuthExperience
              title="Neurorobotics Studio"
              initialView={initialView}
              providerChooserFirst={providerChooserFirst}
              neuravilleLogoSrc="/neuraville-logo.svg"
              providerButtonPrefix="Continue"
              onOAuthLogin={async (provider) => {
                oauthInFlightProviderRef.current = provider;
                lastProviderSelectedRef.current = provider;
                if (shouldTrackDownloadFunnel) {
                  try {
                    sessionStorage.setItem(PENDING_AUTH_PROVIDER_KEY, provider);
                  } catch {
                    // sessionStorage unavailable
                  }
                }
                try {
                  await auth.signinWithProvider(provider, returnPath);
                } catch (error) {
                  trackAuthFailure(provider, error);
                  throw error;
                }
              }}
              onAuthenticated={async (user) => {
                const token =
                  typeof user?.access_token === "string"
                    ? user.access_token.trim()
                    : "";
                if (!token) {
                  const err = new Error(
                    "Login succeeded but no access token was returned for Firebase sign-in."
                  );
                  console.error("[SignupModal] onAuthenticated:", err, user);
                  throw err;
                }
                try {
                  await auth.callSignInWithCustomToken(
                    token,
                    user?.consent_required
                  );
                } catch (e) {
                  console.error(
                    "[SignupModal] Firebase sign-in failed:",
                    e?.code,
                    e?.message,
                    e
                  );
                  throw e;
                }
                const provider =
                  oauthInFlightProviderRef.current ||
                  lastProviderSelectedRef.current ||
                  "neuraville";
                authSucceededRef.current = true;
                if (shouldTrackDownloadFunnel) {
                  recordNrsDownloadFunnelEvent({
                    eventType: "auth_callback_success",
                    provider,
                    source: pathname || undefined,
                    flowId: funnelFlowId,
                    visitId: funnelVisitId,
                  });
                }
                if (funnelFlowId) {
                  try {
                    sessionStorage.setItem(
                      `${AUTH_SUCCESS_RECORDED_FLOW_KEY_PREFIX}${funnelFlowId}`,
                      "1"
                    );
                  } catch {
                    // sessionStorage unavailable
                  }
                }
                try {
                  sessionStorage.removeItem(PENDING_AUTH_PROVIDER_KEY);
                } catch {
                  // sessionStorage unavailable
                }
                handleClose();
                if (typeof onAuthComplete === "function") {
                  onAuthComplete();
                } else {
                  router.push(returnPath);
                }
              }}
              onAuthError={(error) => {
                // The shared email/password form is the only surface behind
                // this callback, so the provider is always "neuraville".
                trackAuthFailure("neuraville", error);
                if (isExpectedAuthFailure(error)) {
                  console.warn("[SignupModal] Expected auth failure:", {
                    status: error?.status,
                    detail: error?.body?.detail,
                    message: error?.message,
                  });
                  return;
                }
                console.error("[SignupModal] Shared auth error:", error);
              }}
              onOpenTerms={() => {
                handleClose();
                router.push("/legal/terms-of-service");
              }}
              onOpenPrivacy={() => {
                handleClose();
                router.push("/legal/privacy-policy");
              }}
              showLegalAgreement
            />
          </Paper>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SignupModal;
